// web-frontend/src/shared/stores/meetingsStore.js
// 🔥🔥🔥 웹 환경 호환 버전 (react-native 의존성 제거)
// 🔥🔥🔥 2025-02-12 수정: showAlert 제거 - ChatRoom에서 Toast로 통합 처리
// 🔥🔥🔥 2025-12-31 수정: 미팅 수락 시 meetings/upcomingMeetings 배열 동기화 문제 해결
//       - acceptMeetingInvitation에서 미팅이 meetings 배열에 없어도 정상 추가되도록 수정
//       - fetchUpcomingMeetings에서 upcomingMeetings를 직접 설정하도록 수정
import { create } from 'zustand';
import meetingsService from '../services/meetingsService';
import localNotificationService from '../services/localNotificationService';
import logger from '../utils/logger';

const TERMINAL_MEETING_STATUSES = new Set(['cancelled', 'completed', 'no_show']);
const TERMINAL_INVITATION_STATUSES = new Set(['cancelled', 'declined', 'rejected']);

const isTerminalMeetingState = (meeting = {}) => {
  const status = String(meeting.status || '').toLowerCase();
  const invitationStatus = String(meeting.invitation_status || '').toLowerCase();
  return TERMINAL_MEETING_STATUSES.has(status) || TERMINAL_INVITATION_STATUSES.has(invitationStatus);
};

const useMeetingsStore = create((set, get) => ({
  // =================== 상태 ===================
  
  meetings: [],
  upcomingMeetings: [],
  pastMeetings: [],
  selectedMeeting: null,
  
  // 필터링 및 UI 상태
  selectedDate: new Date().toISOString().split('T')[0],
  calendarView: 'month',
  statusFilter: 'all',
  typeFilter: 'all',
  
  // 로딩 상태
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  
  // 캐시
  lastFetchTime: null,
  cacheExpiry: 5 * 60 * 1000, // 5분
  
  // 🆕 채팅 연동 상태
  pendingProposals: [],
  invitations: [],
  
  // 🆕 캘린더/알림 상태
  calendarSyncEnabled: true,
  notificationsSyncEnabled: true,
  
  // =================== 🆕 채팅 연동 기능 ===================
  
  /**
   * 채팅에서 미팅 제안
   */
  proposeMeetingFromChat: async (proposalData) => {
    try {
      logger.debug('📤 채팅 미팅 제안:', proposalData);
      
      set({ isCreating: true, error: null });
      
      const result = await meetingsService.proposeMeetingFromChat({
        chatId: proposalData.chatId,
        receiverUserId: proposalData.receiverUserId,
        meetingType: proposalData.meetingType || 'business',
        proposedDatetime: proposalData.proposedDatetime,
        durationMinutes: proposalData.durationMinutes || 60,
        locationType: proposalData.locationType || 'booth',
        locationDetails: proposalData.locationDetails,
        boothId: proposalData.boothId,
        agenda: proposalData.agenda,
        notes: proposalData.notes,
        messageContent: proposalData.messageContent
      });
      
      set(state => ({
        pendingProposals: [...state.pendingProposals, result],
        isCreating: false,
        error: null
      }));
      
      logger.debug('✅ 채팅 미팅 제안 성공');
      return result;
      
    } catch (error) {
      logger.error('❌ 채팅 미팅 제안 실패:', error);
      set({ 
        isCreating: false, 
        error: error.message || 'Unknown error' 
      });
      throw error;
    }
  },
  
  /**
   * 🔥🔥🔥 [2025-12-31 핵심 수정] 미팅 초대 수락 - 미팅이 meetings 배열에 없어도 정상 추가되도록 수정
   */
  fetchAvailabilityMatrix: async ({ chatId, counterpartId, durationMinutes = 60 }) => {
    try {
      set({ error: null });
      const result = await meetingsService.getAvailabilityMatrix({
        chatId,
        counterpartId,
        durationMinutes,
      });
      set({ error: null });
      return result;
    } catch (error) {
      logger.error('미팅 시간표 조회 실패:', error);
      set({ error: error.message || 'Unknown error' });
      throw error;
    }
  },

  createCandidateProposalFromChat: async (proposalData) => {
    try {
      set({ isCreating: true, error: null });
      const result = await meetingsService.createCandidateProposalFromChat(proposalData);
      set(state => ({
        pendingProposals: [...state.pendingProposals, result],
        isCreating: false,
        error: null,
      }));
      return result;
    } catch (error) {
      logger.error('후보형 미팅 제안 실패:', error);
      set({ isCreating: false, error: error.message || 'Unknown error' });
      throw error;
    }
  },

  acceptProposalSlotInvitation: async (messageId, slotIndex) => {
    try {
      set({ isUpdating: true, error: null });
      const result = await meetingsService.acceptProposalSlot(messageId, slotIndex);
      const meeting = result?.meeting;
      set(state => ({
        isUpdating: false,
        error: null,
        meetings: meeting ? [...state.meetings.filter(item => item.id !== meeting.id), meeting] : state.meetings,
        upcomingMeetings: meeting
          ? [...state.upcomingMeetings.filter(item => item.id !== meeting.id), meeting]
          : state.upcomingMeetings,
      }));
      return result;
    } catch (error) {
      logger.error('후보 슬롯 수락 실패:', error);
      set({ isUpdating: false, error: error.message || 'Unknown error' });
      throw error;
    }
  },

  declineCandidateProposal: async (messageId, reason = null) => {
    try {
      set({ isUpdating: true, error: null });
      const result = await meetingsService.declineProposal(messageId, reason || '');
      set({ isUpdating: false, error: null });
      return result;
    } catch (error) {
      logger.error('후보형 미팅 거절 실패:', error);
      set({ isUpdating: false, error: error.message || 'Unknown error' });
      throw error;
    }
  },

  acceptMeetingInvitation: async (meetingId, message = null) => {
    try {
      logger.debug('='.repeat(80));
      logger.debug('📤 미팅 초대 수락 시작:', meetingId);
      
      set({ isUpdating: true, error: null });
      
      // 🔥🔥🔥 [핵심 수정 1] 먼저 미팅 정보를 찾기 (meetings 또는 upcomingMeetings에서)
      const currentState = get();
      let targetMeeting = currentState.meetings.find(m => m.id === meetingId);
      const previousMeetingState = {
        meetings: currentState.meetings,
        upcomingMeetings: currentState.upcomingMeetings,
      };
      
      // meetings에 없으면 upcomingMeetings에서 찾기
      if (!targetMeeting) {
        targetMeeting = currentState.upcomingMeetings.find(m => m.id === meetingId);
        logger.debug('ℹ️ meetings에서 못 찾음, upcomingMeetings에서 검색:', targetMeeting ? '발견' : '없음');
      }
      
      // 🔥🔥🔥 [핵심 수정 2] 로컬 상태를 즉시 업데이트 (백엔드 응답 전에!)
      set(state => {
        const now = new Date().toISOString();
        
        // 🔥 기존 meetings 배열 업데이트
        let updatedMeetings = state.meetings.map(m => {
          if (m.id === meetingId) {
            logger.debug('🔄 meetings 배열에서 미팅 업데이트:', {
              기존: { status: m.status, invitation_status: m.invitation_status },
              변경: { status: 'confirmed', invitation_status: 'accepted' }
            });
            return {
              ...m,
              status: 'confirmed',
              invitation_status: 'accepted',
              updated_at: now,
            };
          }
          return m;
        });
        
        // 🔥🔥🔥 [핵심 추가] meetings 배열에 해당 미팅이 없었다면 추가
        const existsInMeetings = state.meetings.some(m => m.id === meetingId);
        if (!existsInMeetings && targetMeeting) {
          logger.debug('➕ meetings 배열에 미팅 추가 (기존에 없었음):', meetingId);
          const newMeeting = {
            ...targetMeeting,
            status: 'confirmed',
            invitation_status: 'accepted',
            updated_at: now,
          };
          updatedMeetings = [...updatedMeetings, newMeeting];
        }
        
        // 🔥🔥🔥 [핵심 수정 3] upcomingMeetings도 직접 업데이트
        let updatedUpcoming = state.upcomingMeetings.map(m => {
          if (m.id === meetingId) {
            logger.debug('🔄 upcomingMeetings 배열에서 미팅 업데이트:', meetingId);
            return {
              ...m,
              status: 'confirmed',
              invitation_status: 'accepted',
              updated_at: now,
            };
          }
          return m;
        });
        
        // upcomingMeetings에도 없었다면 추가
        const existsInUpcoming = state.upcomingMeetings.some(m => m.id === meetingId);
        if (!existsInUpcoming && targetMeeting) {
          const meetingDate = new Date(targetMeeting.scheduled_start || targetMeeting.scheduled_at);
          const isUpcoming = meetingDate >= new Date();
          
          if (isUpcoming) {
            logger.debug('➕ upcomingMeetings 배열에 미팅 추가 (기존에 없었음):', meetingId);
            const newMeeting = {
              ...targetMeeting,
              status: 'confirmed',
              invitation_status: 'accepted',
              updated_at: now,
            };
            updatedUpcoming = [...updatedUpcoming, newMeeting];
          }
        }
        
        logger.debug('✅ 로컬 상태 업데이트 완료:', {
          meetings: updatedMeetings.length,
          upcomingMeetings: updatedUpcoming.length,
          targetMeetingFound: !!targetMeeting
        });
        
        return {
          meetings: updatedMeetings,
          upcomingMeetings: updatedUpcoming,
        };
      });
      
      // 🔥🔥🔥 [핵심 수정 4] 백엔드 API 호출 (응답은 참고용으로만 사용)
      try {
        const result = await meetingsService.acceptMeeting(meetingId, message);
        logger.debug('ℹ️ 백엔드 수락 응답 (참고용):', result);
        
        // 백엔드 응답이 성공하면 최신 데이터로 한번 더 동기화
        if (result && result.status === 'confirmed') {
          set(state => ({
            meetings: state.meetings.map(m => 
              m.id === meetingId ? { ...m, ...result } : m
            ),
            upcomingMeetings: state.upcomingMeetings.map(m => 
              m.id === meetingId ? { ...m, ...result } : m
            ),
          }));
        }
      } catch (apiError) {
        logger.error('⚠️ 백엔드 미팅 수락 실패 - 로컬 상태 롤백:', apiError);
        set({
          ...previousMeetingState,
          isUpdating: false,
          error: apiError.message || 'Unknown error',
        });
        throw apiError;
      }
      
      // 🔥🔥🔥 3. 알림 스케줄링
      const meeting = get().meetings.find(m => m.id === meetingId);
      if (meeting && get().notificationsSyncEnabled) {
        try {
          await localNotificationService.scheduleMeetingNotifications(meeting);
          logger.debug('✅ 알림 스케줄 완료');
        } catch (error) {
          logger.warn('⚠️ 알림 스케줄 실패:', error);
        }
      }
      
      set({ isUpdating: false, error: null });
      
      logger.debug('✅ 미팅 초대 수락 완료');
      logger.debug('='.repeat(80));
      
      return { success: true, meetingId };
      
    } catch (error) {
      logger.error('❌ 미팅 초대 수락 실패:', error);
      
      // 서버 응답 실패 시 위에서 낙관 업데이트를 되돌린 뒤 오류를 그대로 노출한다.
      set({ 
        isUpdating: false, 
        error: error.message || 'Unknown error' 
      });
      
      throw error;
    }
  },
  
  /**
   * 미팅 초대 거절
   * 🔥🔥🔥 2025-12-31 수정: 로직 개선
   */
  declineMeetingInvitation: async (meetingId, reason = null) => {
    try {
      logger.debug('📤 미팅 초대 거절:', meetingId);
      
      set({ isUpdating: true, error: null });
      const previousMeetingState = {
        meetings: get().meetings,
        upcomingMeetings: get().upcomingMeetings,
      };
      
      // 🔥 먼저 로컬 상태 업데이트
      set(state => {
        const now = new Date().toISOString();
        
        const updatedMeetings = state.meetings.map(m => {
          if (m.id === meetingId) {
            return {
              ...m,
              status: 'cancelled',
              invitation_status: 'declined',
              updated_at: now,
            };
          }
          return m;
        });
        
        // upcomingMeetings에서는 거절된 미팅 제거
        const updatedUpcoming = state.upcomingMeetings.filter(m => m.id !== meetingId);
        
        return {
          meetings: updatedMeetings,
          upcomingMeetings: updatedUpcoming,
        };
      });
      
      // 백엔드 API 호출 (응답 무시)
      try {
        await meetingsService.declineMeeting(meetingId, reason);
      } catch (apiError) {
        logger.error('⚠️ 백엔드 미팅 거절 실패 - 로컬 상태 롤백:', apiError);
        set({
          ...previousMeetingState,
          isUpdating: false,
          error: apiError.message || 'Unknown error',
        });
        throw apiError;
      }
      
      set({ isUpdating: false, error: null });
      
      logger.debug('✅ 미팅 초대 거절 완료');
      return { success: true, meetingId };
      
    } catch (error) {
      logger.error('❌ 미팅 초대 거절 실패:', error);
      set({ 
        isUpdating: false, 
        error: error.message || 'Unknown error' 
      });
      throw error;
    }
  },
  
  /**
   * 알림 포함 미팅 조회
   */
  fetchMeetingWithNotifications: async (meetingId) => {
    try {
      logger.debug('📋 알림 포함 미팅 조회:', meetingId);
      
      set({ isLoading: true, error: null });
      
      const meeting = await meetingsService.getMeetingWithNotifications(meetingId);
      
      set(state => ({
        selectedMeeting: meeting,
        meetings: state.meetings.map(m => 
          m.id === meetingId ? meeting : m
        ),
        isLoading: false,
        error: null
      }));
      
      logger.debug('✅ 알림 포함 미팅 조회 완료');
      return meeting;
      
    } catch (error) {
      logger.error('❌ 알림 포함 미팅 조회 실패:', error);
      set({ 
        isLoading: false, 
        error: error.message || 'Unknown error' 
      });
      throw error;
    }
  },
  
  // =================== 기본 미팅 기능 ===================
  
  /**
   * 🔥🔥🔥 [2025-12-31 수정] 다가오는 미팅 조회 - 로컬 상태 보존 로직 개선
   */
  fetchUpcomingMeetings: async (params = {}) => {
    try {
      logger.debug('📋 fetchUpcomingMeetings 시작:', params);
      
      set({ isLoading: true, error: null });
      
      const apiParams = {
        days: Number(params.days) || 7,
        limit: Number(params.limit) || 50,
      };
      
      logger.debug('📤 getUpcomingMeetings API 호출:', apiParams);
      
      const meetings = await meetingsService.getUpcomingMeetings(apiParams);
      
      const validMeetings = Array.isArray(meetings) ? meetings.filter(meeting => {
        return meeting && typeof meeting === 'object' && meeting.id;
      }) : [];
      
      logger.debug('✅ fetchUpcomingMeetings 완료:', validMeetings.length, '개');
      
      // 🔥🔥🔥 [핵심 수정] meetings 배열과 upcomingMeetings 배열 모두 업데이트
      set(state => {
        const existingMeetings = state.meetings || [];
        const meetingMap = new Map();
        
        // 1. 기존 meetings 추가
        existingMeetings.forEach(m => {
          if (m && m.id) {
            meetingMap.set(m.id, m);
          }
        });
        
        // 2. 새로운 API 응답으로 업데이트/추가
        validMeetings.forEach(m => {
          if (m && m.id) {
            const existing = meetingMap.get(m.id);
            const remoteTerminal = isTerminalMeetingState(m);
            
            // 🔥🔥🔥 [핵심 수정] 로컬에서 이미 수락(accepted)한 미팅은 
            // 백엔드 응답이 아직 proposed/pending일 때만 보존
            // 백엔드 응답도 accepted/confirmed면 백엔드 데이터 사용 (더 정확)
            if (existing && !remoteTerminal) {
              const localAccepted = existing.invitation_status === 'accepted' || 
                                   existing.status === 'confirmed';
              const remoteNotAccepted = m.invitation_status !== 'accepted' && 
                                        m.status !== 'confirmed';
              
              if (localAccepted && remoteNotAccepted) {
                logger.debug('🔒 로컬에서 이미 수락한 미팅 보존:', m.id);
                // 기존 상태 유지 (덮어쓰지 않음)
              } else {
                // 백엔드 응답이 더 최신이거나 동일하면 덮어쓰기
                meetingMap.set(m.id, m);
              }
            } else {
              meetingMap.set(m.id, m);
            }
          }
        });
        
        const updatedMeetings = Array.from(meetingMap.values());
        
        // 🔥🔥🔥 [핵심 수정] upcomingMeetings는 API 응답을 직접 사용하되,
        // 로컬에서 수락한 미팅도 포함되도록 처리
        const upcomingMap = new Map();
        
        // API 응답 추가
        validMeetings.forEach(m => {
          if (m && m.id) {
            upcomingMap.set(m.id, m);
          }
        });
        
        // 로컬에서 수락한 미팅 중 upcoming인 것 추가
        existingMeetings.forEach(m => {
          if (m && m.id && !upcomingMap.has(m.id)) {
            const isAccepted = m.invitation_status === 'accepted' || m.status === 'confirmed';
            const meetingDate = new Date(m.scheduled_start || m.scheduled_at);
            const isUpcoming = meetingDate >= new Date();
            
            if (isAccepted && isUpcoming && !isTerminalMeetingState(m)) {
              logger.debug('➕ 로컬에서 수락한 미팅을 upcomingMeetings에 추가:', m.id);
              upcomingMap.set(m.id, m);
            }
          }
        });
        
        const updatedUpcoming = Array.from(upcomingMap.values());
        
        logger.debug('🔄 상태 업데이트:', {
          meetings: updatedMeetings.length,
          upcomingMeetings: updatedUpcoming.length
        });
        
        return {
          upcomingMeetings: updatedUpcoming,
          meetings: updatedMeetings,
          isLoading: false,
          error: null,
          lastFetchTime: new Date().toISOString(),
        };
      });
      
      return validMeetings;
      
    } catch (error) {
      logger.error('❌ fetchUpcomingMeetings 실패:', error);
      
      set({
        isLoading: false,
        error: error.message || 'Unknown error',
      });
      
      throw error;
    }
  },
  
  /**
   * 🔥🔥🔥 전체 미팅 목록 조회 - 로컬에서 수락한 미팅 보존
   */
  fetchMeetings: async (refresh = false) => {
    const { meetings, lastFetchTime, cacheExpiry } = get();
    
    if (!refresh && meetings.length > 0 && lastFetchTime) {
      const cacheAge = Date.now() - new Date(lastFetchTime).getTime();
      if (cacheAge < cacheExpiry) {
        logger.debug('📋 캐시된 미팅 데이터 사용');
        return meetings;
      }
    }
    
    set({ isLoading: true, error: null });
    
    try {
      logger.debug('📋 전체 미팅 목록 조회 시작 (refresh=' + refresh + ')');
      
      const response = await meetingsService.getMeetings();
      
      logger.debug('📦 getMeetings 응답 타입:', {
        isObject: typeof response === 'object',
        isArray: Array.isArray(response),
        hasMeetings: !!response?.meetings,
        meetingsLength: response?.meetings?.length || 0,
        total: response?.total || 0,
      });
      
      // 응답에서 meetings 배열 추출
      let validMeetings = [];
      
      if (Array.isArray(response)) {
        validMeetings = response;
      } else if (response && typeof response === 'object') {
        if (Array.isArray(response.meetings)) {
          validMeetings = response.meetings;
        } else if (Array.isArray(response.data)) {
          validMeetings = response.data;
        }
      }
      
      logger.debug('✅ 백엔드에서 받은 미팅 개수:', validMeetings.length);
      
      // 🔥🔥🔥 핵심: 로컬에서 수락한 미팅은 백엔드 응답으로 덮어쓰지 않음
      set(state => {
        const existingMeetings = state.meetings || [];
        const meetingMap = new Map();
        
        // 1. 백엔드 응답 추가
        validMeetings.forEach(m => {
          if (m && m.id) {
            meetingMap.set(m.id, m);
          }
        });
        
        // 2. 로컬에서 이미 수락한 미팅은 덮어쓰지 않음
        existingMeetings.forEach(m => {
          if (m && m.id) {
            const localAccepted = m.invitation_status === 'accepted' || m.status === 'confirmed';
            const existing = meetingMap.get(m.id);
            const remoteNotAccepted = existing && 
                                      existing.invitation_status !== 'accepted' && 
                                      existing.status !== 'confirmed';
            const remoteTerminal = existing && isTerminalMeetingState(existing);
             
            if (localAccepted && remoteNotAccepted && !remoteTerminal) {
              logger.debug('🔒 로컬에서 이미 수락한 미팅 보존:', m.id);
              meetingMap.set(m.id, m); // 기존 상태 유지
            }
          }
        });
        
        const finalMeetings = Array.from(meetingMap.values());
        
        const now = new Date();
        const upcoming = finalMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.scheduled_start || meeting.scheduled_at);
          return meetingDate >= now && !isTerminalMeetingState(meeting);
        });
        
        const past = finalMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.scheduled_start || meeting.scheduled_at);
          return meetingDate < now || ['completed'].includes(meeting.status);
        });
        
        return {
          meetings: finalMeetings,
          upcomingMeetings: upcoming,
          pastMeetings: past,
          isLoading: false,
          lastFetchTime: new Date().toISOString(),
          error: null,
        };
      });
      
      logger.debug('✅ 전체 미팅 조회 완료');
      return get().meetings;
      
    } catch (error) {
      logger.error('❌ fetchMeetings 실패:', error);
      set({ 
        error: error.message || 'Unknown error', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  /**
   * 미팅 상세 정보 조회
   */
  fetchMeetingById: async (meetingId) => {
    set({ isLoading: true, error: null });
    
    try {
      logger.debug('📋 미팅 상세 조회:', meetingId);
      
      const meeting = await meetingsService.getMeetingDetail(meetingId);
      
      set(state => ({
        selectedMeeting: meeting,
        meetings: state.meetings.map(m => 
          m.id === meetingId ? { ...m, ...meeting } : m
        ),
        isLoading: false,
        error: null,
      }));
      
      logger.debug('✅ 미팅 상세 조회 완료');
      return meeting;
      
    } catch (error) {
      logger.error('❌ fetchMeetingById 실패:', error);
      set({ 
        error: error.message || 'Unknown error', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  /**
   * 미팅 생성
   */
  createMeeting: async (meetingData) => {
    set({ isCreating: true, error: null });
    
    try {
      logger.debug('📋 미팅 생성:', meetingData);
      
      const newMeeting = await meetingsService.createMeeting(meetingData);
      
      // 생성 후 목록 갱신
      await get().fetchMeetings(true);
      await get().fetchUpcomingMeetings({ days: 30, limit: 10 });
      
      // 알림 스케줄링
      if (get().notificationsSyncEnabled) {
        try {
          await localNotificationService.scheduleMeetingNotifications(newMeeting);
        } catch (error) {
          logger.warn('알림 스케줄 실패:', error);
        }
      }
      
      set({ isCreating: false, error: null });
      
      logger.debug('✅ 미팅 생성 완료');
      return newMeeting;
      
    } catch (error) {
      logger.error('❌ createMeeting 실패:', error);
      set({ 
        error: error.message || 'Unknown error', 
        isCreating: false 
      });
      throw error;
    }
  },
  
  /**
   * 미팅 요청
   */
  requestMeeting: async (requestData) => {
    set({ isCreating: true, error: null });
    
    try {
      logger.debug('📋 미팅 요청:', requestData);
      
      const result = await meetingsService.requestMeeting(requestData);
      
      await get().fetchMeetings(true);
      await get().fetchUpcomingMeetings();
      
      set({ isCreating: false, error: null });
      
      logger.debug('✅ 미팅 요청 완료');
      return result;
      
    } catch (error) {
      logger.error('❌ requestMeeting 실패:', error);
      set({ 
        error: error.message || 'Unknown error', 
        isCreating: false 
      });
      throw error;
    }
  },
  
  /**
   * 미팅 상태 업데이트
   */
  updateMeeting: async (meetingId, updateData) => {
    set({ isUpdating: true, error: null });
    
    try {
      logger.debug('📋 미팅 업데이트:', meetingId, updateData);
      
      const updatedMeeting = await meetingsService.updateMeeting(meetingId, updateData);
      
      await get().fetchMeetings(true);
      await get().fetchUpcomingMeetings();
      
      set({ isUpdating: false, error: null });
      
      logger.debug('✅ 미팅 업데이트 완료');
      return updatedMeeting;
      
    } catch (error) {
      logger.error('❌ updateMeeting 실패:', error);
      set({ 
        error: error.message || 'Unknown error', 
        isUpdating: false 
      });
      throw error;
    }
  },
  
  /**
   * 미팅 취소
   */
  cancelMeeting: async (meetingId, reason) => {
    try {
      logger.debug('📋 미팅 취소:', meetingId);
      
      const result = await meetingsService.cancelMeeting(meetingId, reason);
      const now = new Date().toISOString();
      set(state => ({
        meetings: (state.meetings || []).map(meeting =>
          String(meeting.id) === String(meetingId)
            ? {
                ...meeting,
                status: 'cancelled',
                invitation_status: 'cancelled',
                cancellation_reason: reason,
                updated_at: now,
              }
            : meeting
        ),
        upcomingMeetings: (state.upcomingMeetings || []).filter(meeting => String(meeting.id) !== String(meetingId)),
        selectedMeeting: String(state.selectedMeeting?.id || '') === String(meetingId)
          ? {
              ...state.selectedMeeting,
              status: 'cancelled',
              invitation_status: 'cancelled',
              cancellation_reason: reason,
              updated_at: now,
            }
          : state.selectedMeeting,
      }));
      
      await get().fetchMeetings(true);
      await get().fetchUpcomingMeetings();
      
      // 알림 취소
      if (get().notificationsSyncEnabled) {
        try {
          await localNotificationService.cancelMeetingNotifications(meetingId);
        } catch (error) {
          logger.warn('알림 취소 실패:', error);
        }
      }
      
      logger.debug('✅ 미팅 취소 완료');
      return result;
      
    } catch (error) {
      logger.error('❌ cancelMeeting 실패:', error);
      throw error;
    }
  },
  
  /**
   * 미팅 일정 변경
   */
  rescheduleMeeting: async (meetingId, newDatetime, reason) => {
    try {
      logger.debug('📋 미팅 일정 변경:', meetingId);
      
      const result = await meetingsService.rescheduleMeeting(meetingId, newDatetime, reason);
      
      await get().fetchMeetings(true);
      await get().fetchUpcomingMeetings();
      
      const meeting = get().meetings.find(m => m.id === meetingId);
      
      // 알림 재스케줄
      if (meeting && get().notificationsSyncEnabled) {
        try {
          await localNotificationService.rescheduleMeetingNotifications(meeting);
        } catch (error) {
          logger.warn('알림 재스케줄 실패:', error);
        }
      }
      
      logger.debug('✅ 미팅 일정 변경 완료');
      return result;
      
    } catch (error) {
      logger.error('❌ rescheduleMeeting 실패:', error);
      throw error;
    }
  },
  
  /**
   * 미팅 피드백 제출
   */
  submitFeedback: async (meetingId, rating, feedback) => {
    try {
      logger.debug('📋 피드백 제출:', meetingId, rating);
      
      const result = await meetingsService.submitFeedback(meetingId, rating, feedback);
      
      set(state => ({
        meetings: state.meetings.map(m =>
          m.id === meetingId ? { ...m, rating, feedback } : m
        ),
        selectedMeeting: state.selectedMeeting?.id === meetingId ? 
          { ...state.selectedMeeting, rating, feedback } : state.selectedMeeting,
      }));
      
      logger.debug('✅ 피드백 제출 완료');
      return result;
      
    } catch (error) {
      logger.error('❌ submitFeedback 실패:', error);
      throw error;
    }
  },
  
  // =================== UI 상태 관리 ===================
  
  setSelectedDate: (date) => {
    set({ selectedDate: date });
  },
  
  setCalendarView: (view) => {
    set({ calendarView: view });
  },
  
  setStatusFilter: (status) => {
    set({ statusFilter: status });
  },
  
  setTypeFilter: (type) => {
    set({ typeFilter: type });
  },
  
  selectMeeting: (meeting) => {
    set({ selectedMeeting: meeting });
  },
  
  clearSelectedMeeting: () => {
    set({ selectedMeeting: null });
  },
  
  // =================== 캘린더/알림 설정 ===================
  
  setCalendarSyncEnabled: (enabled) => {
    set({ calendarSyncEnabled: enabled });
  },
  
  setNotificationsSyncEnabled: (enabled) => {
    set({ notificationsSyncEnabled: enabled });
  },
  
  // =================== 데이터 조회 유틸리티 ===================
  
  /**
   * 날짜별 미팅 조회
   */
  getMeetingsForDate: (date) => {
    const { meetings } = get();
    const targetDate = new Date(date).toISOString().split('T')[0];
    
    return meetings.filter(meeting => {
      const meetingDateStr = meeting.scheduled_start || meeting.scheduled_at;
      if (!meetingDateStr) {
        return false;
      }
      
      const meetingDate = new Date(meetingDateStr);
      const meetingDateOnly = meetingDate.toISOString().split('T')[0];
      
      return meetingDateOnly === targetDate;
    });
  },
  
  getMeetingsForDateRange: (startDate, endDate) => {
    const { meetings } = get();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return meetings.filter(meeting => {
      const meetingDateStr = meeting.scheduled_start || meeting.scheduled_at;
      if (!meetingDateStr) return false;
      
      const meetingDate = new Date(meetingDateStr);
      return meetingDate >= start && meetingDate <= end;
    });
  },
  
  getFilteredMeetings: () => {
    const { meetings, statusFilter, typeFilter } = get();
    
    return meetings.filter(meeting => {
      const statusMatch = statusFilter === 'all' || meeting.status === statusFilter;
      const typeMatch = typeFilter === 'all' || 
        meeting.type === typeFilter || 
        meeting.meeting_type === typeFilter;
      return statusMatch && typeMatch;
    });
  },
  
  getTodaysMeetings: () => {
    const today = new Date().toISOString().split('T')[0];
    return get().getMeetingsForDate(today);
  },
  
  getOverdueMeetings: () => {
    const { meetings } = get();
    const now = new Date();
    
    return meetings.filter(meeting => {
      const meetingDateStr = meeting.scheduled_start || meeting.scheduled_at;
      if (!meetingDateStr) return false;
      
      const meetingDate = new Date(meetingDateStr);
      return meetingDate < now && meeting.status === 'scheduled';
    });
  },
  
  getPendingInvitations: () => {
    const { meetings } = get();
    return meetings.filter(m => 
      m.invitation_status === 'pending' || 
      m.invitation_status === 'proposed'
    );
  },
  
  // =================== 통계 및 상태 조회 ===================
  
  getStats: () => {
    const { meetings, upcomingMeetings, pastMeetings } = get();
    const completed = meetings.filter(m => m.status === 'completed');
    const cancelled = meetings.filter(m => m.status === 'cancelled');
    const pending = meetings.filter(m => m.invitation_status === 'pending');
    
    return {
      total: meetings.length,
      upcoming: upcomingMeetings.length,
      past: pastMeetings.length,
      completed: completed.length,
      cancelled: cancelled.length,
      pendingInvitations: pending.length,
      completionRate: meetings.length > 0 ? Math.round((completed.length / meetings.length) * 100) : 0,
    };
  },
  
  // =================== 캐시 및 상태 관리 ===================
  
  refresh: async () => {
    logger.debug('🔄 미팅 데이터 새로고침');
    try {
      await Promise.all([
        get().fetchMeetings(true),
        get().fetchUpcomingMeetings({ days: 30, limit: 10 })
      ]);
      logger.debug('✅ 새로고침 완료');
    } catch (error) {
      logger.error('❌ 새로고침 실패:', error);
      throw error;
    }
  },
  
  clearError: () => {
    set({ error: null });
  },
  
  clearCache: () => {
    logger.debug('🧹 캐시 정리');
    set({
      meetings: [],
      upcomingMeetings: [],
      pastMeetings: [],
      pendingProposals: [],
      invitations: [],
      lastFetchTime: null,
      error: null,
    });
  },
  
  // =================== 🔥🔥🔥 [신규 추가] 미팅 데이터 강제 동기화 ===================
  
  /**
   * 특정 미팅의 상태를 강제로 업데이트 (WebSocket 이벤트 등에서 사용)
   */
  updateMeetingStatus: (meetingId, newStatus, invitationStatus) => {
    logger.debug('🔄 미팅 상태 강제 업데이트:', { meetingId, newStatus, invitationStatus });
    
    set(state => {
      const now = new Date().toISOString();
      
      // meetings 배열 업데이트
      const updatedMeetings = state.meetings.map(m => {
        if (m.id === meetingId) {
          return {
            ...m,
            status: newStatus,
            invitation_status: invitationStatus,
            updated_at: now,
          };
        }
        return m;
      });
      
      // upcomingMeetings 배열 업데이트
      let updatedUpcoming = state.upcomingMeetings.map(m => {
        if (m.id === meetingId) {
          return {
            ...m,
            status: newStatus,
            invitation_status: invitationStatus,
            updated_at: now,
          };
        }
        return m;
      });
      
      // 취소/거절된 미팅은 upcomingMeetings에서 제거
      if (newStatus === 'cancelled' || invitationStatus === 'declined') {
        updatedUpcoming = updatedUpcoming.filter(m => m.id !== meetingId);
      }
      
      return {
        meetings: updatedMeetings,
        upcomingMeetings: updatedUpcoming,
        selectedMeeting: String(state.selectedMeeting?.id || '') === String(meetingId)
          ? {
              ...state.selectedMeeting,
              status: newStatus,
              invitation_status: invitationStatus,
              updated_at: now,
            }
          : state.selectedMeeting,
      };
    });
  },
  
  /**
   * 미팅을 upcomingMeetings에 직접 추가 (미팅 제안 시 사용)
   */
  addToUpcomingMeetings: (meeting) => {
    if (!meeting || !meeting.id) {
      logger.warn('⚠️ addToUpcomingMeetings: 유효하지 않은 미팅 데이터');
      return;
    }
    
    logger.debug('➕ upcomingMeetings에 미팅 추가:', meeting.id);
    
    set(state => {
      // 중복 체크
      const exists = state.upcomingMeetings.some(m => m.id === meeting.id);
      if (exists) {
        logger.debug('ℹ️ 이미 upcomingMeetings에 존재함:', meeting.id);
        return state;
      }
      
      return {
        upcomingMeetings: [...state.upcomingMeetings, meeting],
        meetings: state.meetings.some(m => m.id === meeting.id) 
          ? state.meetings 
          : [...state.meetings, meeting],
      };
    });
  },
  
  // =================== 디버그 ===================
  
  getDebugInfo: () => {
    const state = get();
    return {
      meetings: state.meetings.length,
      upcomingMeetings: state.upcomingMeetings.length,
      pastMeetings: state.pastMeetings.length,
      pendingProposals: state.pendingProposals.length,
      invitations: state.invitations.length,
      isLoading: state.isLoading,
      error: state.error,
      lastFetchTime: state.lastFetchTime,
      selectedMeeting: state.selectedMeeting?.id || null,
      calendarSyncEnabled: state.calendarSyncEnabled,
      notificationsSyncEnabled: state.notificationsSyncEnabled,
      cacheAge: state.lastFetchTime ? 
        Date.now() - new Date(state.lastFetchTime).getTime() : null,
      timestamp: new Date().toISOString(),
      // 🔥 디버그용: 각 미팅의 상태 요약
      meetingsStatusSummary: state.meetings.map(m => ({
        id: m.id,
        status: m.status,
        invitation_status: m.invitation_status,
      })),
      upcomingStatusSummary: state.upcomingMeetings.map(m => ({
        id: m.id,
        status: m.status,
        invitation_status: m.invitation_status,
      })),
    };
  },
}));

export default useMeetingsStore;
export { useMeetingsStore };
